import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Sparkles, Palette, Calendar } from 'lucide-react';
import { eventTypesAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const EMPTY_FORM = { name: '', slug: '', color: '#1e3a5f', sort_order: 99 };

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const EventTypesManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { toast } = useToast();

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      setTypes(await eventTypesAPI.getAll());
    } catch {
      toast({ title: 'Error loading event types', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const openDialog = (tpl = null) => {
    if (tpl) {
      setEditing(tpl);
      setForm({ name: tpl.name, slug: tpl.slug, color: tpl.color, sort_order: tpl.sort_order });
    } else {
      setEditing(null);
      setForm({ ...EMPTY_FORM, sort_order: (types[types.length - 1]?.sort_order || 0) + 10 });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast({ title: 'Name is required', variant: 'destructive' }); return; }
    const payload = {
      name: form.name,
      slug: form.slug || slugify(form.name),
      color: form.color,
      sort_order: parseInt(form.sort_order) || 0,
    };
    try {
      setSaving(true);
      if (editing) {
        await eventTypesAPI.update(editing.id, payload);
        toast({ title: 'Event type updated' });
      } else {
        await eventTypesAPI.create(payload);
        toast({ title: 'Event type created' });
      }
      setDialogOpen(false);
      fetchTypes();
    } catch (e) {
      toast({
        title: 'Error saving event type',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive'
      });
    } finally { setSaving(false); }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}"? Existing contracts with this type will still work but the label will no longer appear in filters.`)) return;
    try {
      await eventTypesAPI.delete(tpl.id);
      toast({ title: 'Event type deleted' });
      fetchTypes();
    } catch (e) {
      toast({ title: 'Cannot delete', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleSeed = async () => {
    try {
      setSeeding(true);
      const res = await eventTypesAPI.seedDefaults();
      toast({ title: `Seeded ${res.created.length} defaults`, description: res.skipped > 0 ? `${res.skipped} already existed` : undefined });
      fetchTypes();
    } catch { toast({ title: 'Error seeding', variant: 'destructive' }); }
    finally { setSeeding(false); }
  };

  return (
    <div data-testid="event-types-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div>
          <p className="text-gray-600">Event types drive the Contracts event-type dropdown, Calendar colour coding, and BEO layout.</p>
          <p className="text-xs text-gray-500 mt-1">System types (Onsite, Offsite, etc.) can be recoloured and renamed but not deleted.</p>
        </div>
        <div className="flex gap-2">
          {types.length === 0 && (
            <Button onClick={handleSeed} disabled={seeding} variant="outline" className="border-[#c9a227] text-[#c9a227]" data-testid="seed-event-types-btn">
              {seeding ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
              Seed Defaults
            </Button>
          )}
          <Button onClick={() => openDialog()} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="add-event-type-btn">
            <Plus size={18} className="mr-2" />Add Event Type
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#1e3a5f]" size={32} /></div>
      ) : types.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Calendar size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-3">No event types yet. Click "Seed Defaults" to add the 6 standard types (Onsite, Offsite, Tournament, Cocktail Coach, Catering Pick Up, Catering Drop Off).</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {types.map(tpl => (
            <Card key={tpl.id} className="border-none shadow-sm" data-testid={`event-type-card-${tpl.slug}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-2">
                  <span className="w-4 h-12 rounded" style={{ backgroundColor: tpl.color }} />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#1e3a5f] truncate">{tpl.name}</h4>
                    <p className="text-xs text-gray-500 font-mono">{tpl.slug}</p>
                    {tpl.is_system && <span className="inline-block text-[10px] px-1.5 py-0.5 mt-1 rounded bg-gray-100 text-gray-600">system</span>}
                  </div>
                </div>
                <div className="flex justify-end gap-1 pt-2 border-t mt-2">
                  <Button size="sm" variant="ghost" onClick={() => openDialog(tpl)} className="text-[#1e3a5f] h-8" data-testid={`edit-event-type-${tpl.slug}`}>
                    <Pencil size={14} />
                  </Button>
                  {!tpl.is_system && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(tpl)} className="text-red-600 h-8" data-testid={`delete-event-type-${tpl.slug}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">{editing ? 'Edit Event Type' : 'Add Event Type'}</DialogTitle>
            <DialogDescription>Label + colour used across Contracts, Calendar, and BEO.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Name *</Label>
              <Input
                data-testid="event-type-name-input"
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm(f => ({ ...f, name, slug: editing ? f.slug : slugify(name) }));
                }}
                placeholder="e.g., Off-Site Tasting"
              />
            </div>
            <div>
              <Label>Slug (internal identifier)</Label>
              <Input
                data-testid="event-type-slug-input"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                disabled={editing?.is_system}
                placeholder="auto-generated from name"
              />
              <p className="text-[10px] text-gray-400 mt-1">Lowercase letters, digits, underscores. Used internally on contracts — don't change after events have been created with this type.</p>
            </div>
            <div>
              <Label className="flex items-center gap-2"><Palette size={14} /> Colour</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  data-testid="event-type-color-picker"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-14 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  placeholder="#1e3a5f"
                  className="font-mono"
                />
              </div>
            </div>
            <div>
              <Label>Sort Order</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="save-event-type-btn">
              {saving && <Loader2 size={16} className="mr-2 animate-spin" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventTypesManagement;
